import clsx from 'clsx';
import { memo } from 'react';

import '../../styles/index.css';
import type { IconGlyphSize, IconType } from '../../types/SharedIconProps';

import { icons } from '../../styles/tokens/iconography/iconography';
import './Icon.css';
import { getValidIconKey } from './utils';

export const Icon = memo(
  (props: {
    /** Semantic name. */
    icon: IconType;
    /** Layout size. @default 'm' */
    size?: IconGlyphSize;
    /** Internal fill emphasis. @default false */
    filled?: boolean;
    /** Accessible name. */
    label?: string;
  }) => {
    const { icon, label, size = 'm', filled = false } = props;

    const iconKey = getValidIconKey(icon);
    if (iconKey === null) {
      return null;
    }

    const iconEntry = icons[iconKey];
    const className = clsx(
      'eds-icon',
      `eds-icon-size-${size}`,
      `eds-icon--${icon}`,
    );
    const a11yProps = {
      'aria-hidden': label === undefined ? true : undefined,
      'aria-label': label,
      role: label !== undefined ? ('img' as const) : undefined,
      focusable: 'false' as const,
    };

    if (iconEntry.type === 'custom') {
      const Svg =
        filled && iconEntry.filled !== undefined
          ? iconEntry.filled
          : iconEntry.component;
      return (
        <Svg className={className} viewBox={iconEntry.viewBox} {...a11yProps} />
      );
    }

    const Svg =
      filled && iconEntry.filled !== undefined
        ? iconEntry.filled
        : iconEntry.base;

    return (
      <Svg
        className={className}
        viewBox={iconEntry.viewBox}
        fill="currentColor"
        {...a11yProps}
      />
    );
  },
);

Icon.displayName = 'Icon';
